import { OLAF } from 'HubStyleTokens/colors';
import { TABLE_BORDER_COLOR } from 'HubStyleTokens/theme';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import H7 from 'UIComponents/elements/headings/H7';
import { OperationPropType } from '../../../lib/api-explorer/models/Oas';
import parametersToJsonSchema from '../../../lib/api-explorer/parameters-to-json-schema';
import cloneDeep from 'hs-lodash/cloneDeep';
import isEmpty from 'hs-lodash/isEmpty';
import isNil from 'hs-lodash/isNil';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIExpandableSection from 'UIComponents/accordion/UIExpandableSection';
import H5 from 'UIComponents/elements/headings/H5';
import AccordionTransition from 'UIComponents/transitions/AccordionTransition';
import ParamForm from '../../ParamForm/components/ParamForm';
// @ts-expect-error No types given for file
import LazyLoadWrapper from '../../Response/components/LazyLoadWrapper';
import { MICRO_TRANSITION_TIMING } from 'HubStyleTokens/times';
import { EXPANDABLE_OPENER_COLOR } from 'UIComponents/accordion/ExpandableConstants';
import UICaret from 'UIComponents/icon/icons/UICaret';
import { useDebounce } from '../hooks/useDebounce';
// @ts-expect-error No types given for file
import PropertyCollapser from '../utils/PropertyCollapser';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const MAX_PARAMS = 3;
export const EXPANDABLE_PADDING_LEFT = '44px';
const ExpandableCaretHolder = styled.span.withConfig({
  displayName: "NewParams__ExpandableCaretHolder",
  componentId: "sc-324efp-0"
})(["display:flex;flex-shrink:0;justify-content:center;width:", ";"], EXPANDABLE_PADDING_LEFT);
const ExpandableCaret = styled(UICaret).attrs({
  color: EXPANDABLE_OPENER_COLOR,
  height: 12,
  width: 5
}).withConfig({
  displayName: "NewParams__ExpandableCaret",
  componentId: "sc-324efp-1"
})(["transition:all ", " ease-in-out;transform:", ";"], MICRO_TRANSITION_TIMING, props => props.open ? 'rotate(270deg)' : null);
const ParamsWrapper = styled.div.withConfig({
  displayName: "NewParams__ParamsWrapper",
  componentId: "sc-324efp-2"
})(["background-color:", ";"], OLAF);
const Opener = UIExpandableSection.defaultProps.Opener;
const StyledOpener = styled(props => /*#__PURE__*/_jsx(Opener, Object.assign({}, props))).withConfig({
  displayName: "NewParams__StyledOpener",
  componentId: "sc-324efp-3"
})(["padding:0;border-width:1px 0 0 0;border-color:", ";border-style:solid;"], TABLE_BORDER_COLOR);
const NewParams = ({
  formData,
  oas,
  operation,
  onChange,
  usageTracker
}) => {
  const [collapser] = useState(new PropertyCollapser(MAX_PARAMS));
  const [fullSchema, setFullSchema] = useState(null);
  const [state, setState] = useState({
    initialParamsJsonSchema: null,
    expandedParamsJsonSchema: null,
    isCalculated: false
  });
  const [isOpen, setIsOpen] = useState(false);

  // use a debounce on the is open to prevent text flash on page as soon as button is clicked while params are still expanding
  const debouncedIsOpen = useDebounce(isOpen, 200);

  /**
   * calculates both initial schema and expanded schema
   */
  const calculateSchemas = useCallback(() => {
    const full = fullSchema || parametersToJsonSchema(operation, oas);
    setFullSchema(full);
    const collapsedParams = collapser.collapsedSchema || collapser.calculateCollapsedSchema(cloneDeep(full), formData, operation);
    const additionalParameters = collapser.expandedSchema || collapser.calculateExpandedSchema(cloneDeep(full), formData, operation);
    setState({
      initialParamsJsonSchema: collapsedParams,
      expandedParamsJsonSchema: (additionalParameters || []).filter(schema => !isNil(schema.schema) && !isEmpty(schema.schema && schema.schema.properties)),
      isCalculated: true
    });
  }, [collapser, formData, fullSchema, oas, operation]);
  useEffect(() => {
    calculateSchemas();
  }, [calculateSchemas]);
  const renderContent = () => {
    const {
      initialParamsJsonSchema,
      expandedParamsJsonSchema,
      isCalculated
    } = state;
    if ((!initialParamsJsonSchema || !initialParamsJsonSchema.length) && isCalculated) {
      return /*#__PURE__*/_jsx("div", {
        className: "no-params",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "developer-docs-shared-components.params.body.noParams"
        })
      });
    }
    const wrapperId = `wrapper-${operation.operationId}`;
    return /*#__PURE__*/_jsx(LazyLoadWrapper, {
      mountOnce: true,
      children: /*#__PURE__*/_jsxs("div", {
        id: wrapperId,
        children: [isCalculated && /*#__PURE__*/_jsx("div", {
          className: "params-block",
          children: (initialParamsJsonSchema || []).map(schema => /*#__PURE__*/_jsx(ParamForm
          /*  @ts-expect-error Sep-25-2024, 17:26UTC FixMe: Complete Migration to TypeScript  */, {
            formData: formData[schema.type],
            id: `form-${operation.operationId}`,
            idPrefix: operation.operationId,
            onChange: form => onChange({
              [schema.type]: form.formData
            }),
            schema: schema.schema,
            schemaGroup: schema.label,
            usageTracker: usageTracker
          }, `${schema.type}-form`))
        }), expandedParamsJsonSchema && expandedParamsJsonSchema.length > 0 && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(AccordionTransition, {
            open: isOpen,
            children: /*#__PURE__*/_jsx("div", {
              className: "params-block",
              children: (expandedParamsJsonSchema || []).map(schema => {
                return /*#__PURE__*/_jsx(ParamForm
                /*  @ts-expect-error Sep-25-2024, 17:26UTC FixMe: Complete Migration to TypeScript  */, {
                  formData: formData[schema.type],
                  id: `form-${operation.operationId}`,
                  idPrefix: operation.operationId,
                  onChange: form => onChange({
                    [schema.type]: form.formData
                  }),
                  schema: schema.schema,
                  schemaGroup: schema.label,
                  usageTracker: usageTracker
                }, `${schema.type}-form`);
              })
            })
          }), /*#__PURE__*/_jsxs(StyledOpener, {
            "aria-expanded": isOpen ? 'true' : 'false',
            className: "p-all-4",
            onClick: () => {
              if (!isOpen) {
                setIsOpen(true);
              } else {
                setIsOpen(false);
                const wrapperElement = document.getElementById(wrapperId);
                if (wrapperElement) {
                  wrapperElement.scrollIntoView();
                }
              }
            },
            children: [/*#__PURE__*/_jsx(ExpandableCaretHolder, {
              children: /*#__PURE__*/_jsx(ExpandableCaret, {
                open: isOpen
              })
            }), /*#__PURE__*/_jsx(H7, {
              role: "presentation",
              className: "m-all-0",
              children: debouncedIsOpen ? /*#__PURE__*/_jsx(FormattedMessage, {
                message: "developer-docs-shared-components.params.body.newCollapseButton"
              }) : /*#__PURE__*/_jsx(FormattedMessage, {
                message: "developer-docs-shared-components.params.body.newExpandButton"
              })
            })]
          })]
        })]
      })
    });
  };
  return /*#__PURE__*/_jsxs(ParamsWrapper, {
    className: "params",
    children: [/*#__PURE__*/_jsx(H5, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "developer-docs-shared-components.params.header.parameters"
      })
    }), renderContent()]
  });
};
NewParams.propTypes = {
  formData: PropTypes.object.isRequired,
  oas: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  operation: OperationPropType.isRequired,
  usageTracker: PropTypes.object.isRequired
};
export default NewParams;